// src/components/ui/AnimatedDotBackground.tsx
import React from 'react';
import { cn } from '@/lib/utils';

export interface AnimatedDotBackgroundProps {
  className?: string;
  color?: string;
  intensity?: 'light' | 'medium' | 'strong';
  fadeCenter?: boolean;
  enhancedCorners?: boolean;
  speed?: 'slow' | 'medium' | 'fast';
}

/**
 * A component that renders an animated dot background pattern.
 * This can be used in any section to add a subtle tech-inspired background.
 */
const AnimatedDotBackground: React.FC<AnimatedDotBackgroundProps> = ({
  className,
  color = 'hsl(var(--secondary))',
  intensity = 'medium',
  fadeCenter = true,
  enhancedCorners = true,
  speed = 'medium',
}) => {
  // Opacity settings based on intensity
  const opacitySettings = {
    light: {
      base: 'opacity-10',
      layer1: 'opacity-20',
      layer2: 'opacity-15',
      corner: 'opacity-10',
    },
    medium: {
      base: 'opacity-15',
      layer1: 'opacity-30',
      layer2: 'opacity-15',
      corner: 'opacity-20',
    },
    strong: {
      base: 'opacity-15',
      layer1: 'opacity-40',
      layer2: 'opacity-20',
      corner: 'opacity-30',
    },
  };

  // Animation speed settings
  const animationSpeeds = {
    slow: {
      layer1: 'moveDotBg1 180s linear infinite',
      layer2: 'moveDotBg2 150s linear infinite',
      layer3: 'moveDotBg3 120s linear infinite',
      corner1: 'moveDotFocus1 160s linear infinite',
      corner2: 'moveDotFocus2 170s linear infinite',
    },
    medium: {
      layer1: 'moveDotBg1 120s linear infinite',
      layer2: 'moveDotBg2 90s linear infinite',
      layer3: 'moveDotBg3 60s linear infinite',
      corner1: 'moveDotFocus1 100s linear infinite',
      corner2: 'moveDotFocus2 110s linear infinite',
    },
    fast: {
      layer1: 'moveDotBg1 80s linear infinite',
      layer2: 'moveDotBg2 60s linear infinite',
      layer3: 'moveDotBg3 40s linear infinite',
      corner1: 'moveDotFocus1 70s linear infinite',
      corner2: 'moveDotFocus2 75s linear infinite',
    },
  };

  const currentOpacity = opacitySettings[intensity];
  const currentSpeed = animationSpeeds[speed];

  return (
    <>
      {/* Main animated dot pattern background */}
      <div
        className={cn(
          'absolute inset-0 z-0 pointer-events-none overflow-hidden',
          currentOpacity.base,
          className,
        )}>
        {/* Animated pattern layer 1 - smaller dots, slower movement */}
        <div
          className="absolute w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle, ${color} 1.5px, transparent 1.5px)`,
            backgroundSize: '30px 30px',
            animation: currentSpeed.layer1,
          }}></div>

        {/* Animated pattern layer 2 - medium dots, medium speed */}
        <div
          className={cn('absolute w-full h-full', currentOpacity.layer1)}
          style={{
            backgroundImage: `radial-gradient(circle, ${color} 1.5px, transparent 1.5px)`,
            backgroundSize: '40px 40px',
            backgroundPosition: '5px 5px',
            animation: currentSpeed.layer2,
          }}></div>

        {/* Animated pattern layer 3 - larger dots, faster movement */}
        <div
          className={cn('absolute w-full h-full', currentOpacity.layer2)}
          style={{
            backgroundImage: `radial-gradient(circle, ${color} 1.5px, transparent 1.5px)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '15px 15px',
            animation: currentSpeed.layer3,
          }}></div>

        {/* Gradient mask to fade the pattern around the content */}
        {fadeCenter && (
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at center, transparent 20%, white 70%)',
            }}></div>
        )}
      </div>

      {/* Enhanced focus areas with slightly larger dots (conditional) */}
      {enhancedCorners && (
        <>
          <div className="absolute top-0 right-0 w-1/2 h-1/2 z-0 pointer-events-none overflow-hidden">
            <div
              className={cn('absolute w-full h-full', currentOpacity.corner)}
              style={{
                backgroundImage: `radial-gradient(circle, ${color} 2px, transparent 2px)`,
                backgroundSize: '50px 50px',
                animation: currentSpeed.corner1,
              }}>
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'radial-gradient(circle at top right, transparent 30%, white 90%)',
                }}></div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 z-0 pointer-events-none overflow-hidden">
            <div
              className={cn('absolute w-full h-full', currentOpacity.corner)}
              style={{
                backgroundImage: `radial-gradient(circle, ${color} 2px, transparent 2px)`,
                backgroundSize: '50px 50px',
                animation: currentSpeed.corner2,
              }}>
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'radial-gradient(circle at bottom left, transparent 30%, white 90%)',
                }}></div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AnimatedDotBackground;
